import { useState } from "react"

import useDateFormatters from "@hooks/useDateFormatters"
import useHasAccess from "@hooks/useHasAccess"

import { NO_REFERENCE_SENTINEL, findBrandSubscription, findCatalogSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"

import { Bullet, Skeleton } from "@particles/index"

import { Avatar, IconButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { DisplayModule, EmptyState, Prompt } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import {
    useBrandCountsQuery,
    useCatalogBrandsQuery,
    useCatalogResyncMutation,
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"
import CatalogBrandRow from "@pages/Settings/SettingsCatalogs/components/CatalogBrandRow/CatalogBrandRow"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./CatalogCard.module.scss"
import { CatalogCardProps } from "./CatalogCard.types"

export default function CatalogCard(props: CatalogCardProps) {
    const { catalog } = props

    const { formatDatetime } = useDateFormatters()
    const { hasPermission } = useHasAccess()
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const brandsQuery = useCatalogBrandsQuery(catalog.id)
    const brandCountsQuery = useBrandCountsQuery(catalog.id)
    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalog.id)

    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()
    const resyncMutation = useCatalogResyncMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const isSyncing = catalog.last_sync_status === "in_progress"

    const lastSynced = formatDatetime(catalog.last_sync_completed_at)
    const syncCaption = isSyncing ? "Sync in progress…" : lastSynced ? `Last synced ${lastSynced}` : "Never synced"

    const subscriptions = subscriptionsQuery.data ?? []
    const catalogSubscription = findCatalogSubscription(subscriptions)
    const isCatalogSubscribed = catalogSubscription !== null

    const countByBrand = new Map((brandCountsQuery.data ?? []).map((brandCount) => [brandCount.brand, brandCount]))

    // The brand listing carries only real brands; the brandless "No Brand" bucket is a client-side
    // pseudo-row (a null brand) appended when the counts report active items with no brand.
    const brandRows: Array<CatalogBrand | null> = [...(brandsQuery.data ?? [])]
    if (countByBrand.has(NO_REFERENCE_SENTINEL)) {
        brandRows.push(null)
    }

    const isBrandListLoading = brandsQuery.isLoading || brandCountsQuery.isLoading || subscriptionsQuery.isLoading

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate({ catalog: catalog.id, level: "catalog" })
    }

    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate({ catalog: catalog.id, level: "catalog" })
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        if (catalogSubscription !== null) {
            unsubscribeMutation.mutate(catalogSubscription)
        }
    }

    const isActionPending = subscribeMutation.isPending || unsubscribeMutation.isPending || resyncMutation.isPending

    const menuItems: MenuContentNode[] = [
        {
            type: "group",
            items: [
                ...(isCatalogSubscribed
                    ? []
                    : [
                          {
                              title: "Subscribe to catalog",
                              icon: faIcon({ name: "circle-plus", style: "regular" as const }),
                              onClick: handleSubscribeClick,
                          },
                      ]),
                {
                    title: "Resync Catalog",
                    icon: faIcon({ name: "arrows-rotate", style: "regular" }),
                    onClick: () => resyncMutation.mutate(catalog),
                },
            ],
        },
        ...(isCatalogSubscribed
            ? [
                  { type: "divider" as const },
                  {
                      type: "group" as const,
                      items: [
                          {
                              variant: "danger" as const,
                              title: "Unsubscribe from catalog",
                              icon: faIcon({ name: "trash-can", style: "regular" as const }),
                              onClick: () => setIsUnsubscribePromptOpen(true),
                          },
                      ],
                  },
              ]
            : []),
    ]

    const previewCount = previewCountMutation.data?.count
    const ongoingImportClause = " — new items added to this catalog later will be imported automatically too"
    const subscribeBody =
        previewCount === undefined
            ? `This will import this catalog's items into your pricebook and keep them in sync${ongoingImportClause}. Are you sure you want to do this?`
            : `This will import ${previewCount} ${previewCount === 1 ? "item" : "items"} into your pricebook and keep them in sync${ongoingImportClause}. Are you sure you want to do this?`

    const renderBrandList = () => {
        if (isBrandListLoading) {
            return (
                <>
                    <Skeleton height={56} />
                    <Skeleton height={56} />
                    <Skeleton height={56} />
                </>
            )
        }

        if (brandsQuery.isError || brandCountsQuery.isError || subscriptionsQuery.isError) {
            return (
                <EmptyState
                    variant="error"
                    icon={faIcon({ name: "circle-xmark", style: "solid" })}
                    title="Hmm, something went wrong..."
                    supportText="An error occurred while fetching this catalog's brands."
                    primaryCTA={{
                        children: "Try again",
                        onClick: () => {
                            void brandsQuery.refetch()
                            void brandCountsQuery.refetch()
                            void subscriptionsQuery.refetch()
                        },
                    }}
                />
            )
        }

        if (brandRows.length === 0) {
            return (
                <EmptyState
                    variant="default"
                    icon={faIcon({ name: "shelves", style: "solid" })}
                    title="No brands synced yet"
                    supportText="Resync the catalog to pull in the store's brands and items."
                    primaryCTA={
                        hasPricebookEditPermission
                            ? {
                                  children: "Resync catalog",
                                  onClick: () => resyncMutation.mutate(catalog),
                                  isProcessing: resyncMutation.isPending,
                                  isDisabled: isSyncing || resyncMutation.isPending,
                              }
                            : undefined
                    }
                />
            )
        }

        return brandRows.map((brand) => {
            const brandId = brand?.id ?? null
            const count = countByBrand.get(brandId ?? NO_REFERENCE_SENTINEL)
            const brandSubscription = findBrandSubscription(subscriptions, brandId)
            const hasFullItemCoverage =
                count !== undefined && count.item_count > 0 && count.subscribed_item_count >= count.item_count
            const isFullySubscribed = isCatalogSubscribed || brandSubscription !== null || hasFullItemCoverage

            return (
                <CatalogBrandRow
                    key={brandId ?? "no-brand"}
                    catalogId={catalog.id}
                    brand={brand}
                    count={count}
                    brandSubscription={brandSubscription}
                    isFullySubscribed={isFullySubscribed}
                />
            )
        })
    }

    return (
        <>
            <DisplayModule
                className={styles.base}
                label={
                    <>
                        <Avatar
                            variant="company"
                            size="lg"
                            src={catalog.logo_url ?? undefined}
                            fallbackName={catalog.name}
                        />
                        <span>{catalog.name}</span>
                        {isCatalogSubscribed && (
                            <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                                Subscribed
                            </AlertBadge>
                        )}
                        {catalog.last_sync_status === "failed" && (
                            <AlertBadge status="error" size="sm" showIcon={true} isLoading={false}>
                                Sync failed
                            </AlertBadge>
                        )}
                    </>
                }
                titleRight={
                    <span className={styles.syncCaption}>
                        {catalog.platform_display} <Bullet /> {syncCaption}
                    </span>
                }
                headerAction1={
                    hasPricebookEditPermission ? (
                        <Menu
                            popoverSide="bottom"
                            popoverAlign="end"
                            title={catalog.name}
                            trigger={
                                <IconButton
                                    variant="ghost"
                                    size="md"
                                    icon={faIcon({ name: "ellipsis", style: "regular" })}
                                    aria-label="More options"
                                    isProcessing={isActionPending}
                                    isDisabled={isActionPending}
                                />
                            }
                            items={menuItems}
                        />
                    ) : undefined
                }
                contentClassName={styles.brands}
            >
                {renderBrandList()}
            </DisplayModule>

            <Prompt
                title={`Subscribe to ${catalog.name}?`}
                body={subscribeBody}
                isOpen={isSubscribePromptOpen}
                isLoading={previewCountMutation.isPending}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />

            <Prompt
                title={`Unsubscribe from ${catalog.name}?`}
                body="Items already in your pricebook will stay there, but they will no longer receive catalog updates, and new catalog items will no longer be imported automatically."
                isOpen={isUnsubscribePromptOpen}
                confirmText="Unsubscribe"
                onConfirm={handleUnsubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsUnsubscribePromptOpen(false)}
                isDangerousOperation={true}
            />
        </>
    )
}
